import type { RedisClientWrapper } from '../RedisClientWrapper.js';
import type { DestroyIntent, DestroyIntentOpts, MasterStateStore, MasterStateStoreOptions } from './MasterStateStore.js';

const DEFAULT_KEY_PREFIX = 'gotti:';

/**
 * Crash backstop on the members SPILL keys, refreshed by every `SADD` — the
 * same shape (and the same seven days) the gate's per-client index keys use.
 * A spill row's legitimate life is its AREA's life, and an area can sit idle
 * with a stable population for as long as the game lasts, so this is sized
 * beyond any real session rather than to a grace window: a short TTL would
 * delete the member set of a LIVE area, which is strictly worse than the leak
 * it would be bounding.
 *
 * The ordinary paths already delete these keys (`DynamicAreaTable.markClosed`
 * clears an area's members on every teardown, host reap and rollback). This
 * covers only what none of them can — a master that DIES holding the sole
 * knowledge that those rows are dead.
 *
 * Deliberately NOT applied to the destroy-intent hash: an intent is the one
 * durable fact here, its whole purpose is to outlive the master process, and
 * it is bounded instead by the 60s post-boot age-out (a rehydrated intent
 * whose area never re-registers is deleted from the store — and if the master
 * dies before that fires, the NEXT boot rehydrates and ages it out again).
 */
const AREA_MEMBERS_TTL_SECONDS = 60 * 60 * 24 * 7;

/**
 * Redis-backed `MasterStateStore`. Two key shapes, and nothing else — see the
 * interface for why the surface is this small and why no method reads a
 * dynamic-area record:
 *
 *   `{prefix}dyn:members:{areaId}`  SET   — the per-area members spill
 *   `{prefix}intent:destroy`       HASH  — field = areaId, value = JSON intent
 *
 * DEVIATION from the design's sketched data model, which named
 * `intent:destroy:{areaId}` as a per-area hash: enumerating those on boot
 * would need `KEYS`/`SCAN`, which `RedisClientWrapper` deliberately does not
 * expose (and which is a bad idea against a shared production redis — `KEYS`
 * blocks the server, `SCAN` needs cursor plumbing and gives no snapshot).
 * ONE hash keyed by areaId makes `getDestroyIntents` a single `HGETALL` while
 * keeping every write a single-field `HSET`/`HDEL` — still atomic field ops
 * with no read-modify-write, which is what design decision 4 actually
 * requires. The set/hash key names keep the design's `dyn:members:` /
 * `intent:destroy` naming so a deployment's keyspace reads the same.
 */
export class RedisMasterStateStore implements MasterStateStore {
    private readonly prefix: string;

    constructor(private readonly redis: RedisClientWrapper, opts: MasterStateStoreOptions = {}) {
        this.prefix = opts.keyPrefix ?? DEFAULT_KEY_PREFIX;
    }

    private membersKey(areaId: string): string {
        return `${this.prefix}dyn:members:${areaId}`;
    }

    private intentsKey(): string {
        return `${this.prefix}intent:destroy`;
    }

    async addAreaMember(areaId: string, clientId: string): Promise<{ count: number }> {
        const key = this.membersKey(areaId);
        await this.redis.sadd(key, [clientId]);
        // SADD has no inline-expiry form, so this is two commands; a master
        // that dies exactly between them leaves an un-TTL'd set, which the
        // NEXT join for the same area re-expires (EXPIRE on an existing key is
        // free, and SADD does not clear a TTL). Named rather than hidden — the
        // same residual `addClientArea`/`addClientToConnector` document.
        await this.redis.expire(key, AREA_MEMBERS_TTL_SECONDS);
        // SCARD, not a local counter: the count has to be the SET's, or a
        // duplicate join report would inflate it. See the interface.
        return { count: await this.redis.scard(key) };
    }

    async removeAreaMember(areaId: string, clientId: string): Promise<{ empty: boolean, count: number }> {
        const key = this.membersKey(areaId);
        await this.redis.srem(key, [clientId]);
        const count = await this.redis.scard(key);
        return { empty: count === 0, count };
    }

    async getAreaMembers(areaId: string): Promise<string[]> {
        const members = await this.redis.smembers(this.membersKey(areaId));
        return members.map((m) => String(m));
    }

    async clearAreaMembers(areaId: string): Promise<void> {
        await this.redis.del(this.membersKey(areaId));
    }

    async putDestroyIntent(areaId: string, intent: { opts: DestroyIntentOpts, generation: number }): Promise<void> {
        await this.redis.hset(this.intentsKey(), areaId, JSON.stringify({
            opts: { drain: intent.opts?.drain, reason: intent.opts?.reason },
            generation: Number(intent.generation),
        }));
    }

    async getDestroyIntents(): Promise<DestroyIntent[]> {
        const hash = await this.redis.hgetall(this.intentsKey());
        if (!hash) return [];
        const out: DestroyIntent[] = [];
        for (const areaId of Object.keys(hash)) {
            let parsed: any;
            try {
                parsed = JSON.parse(hash[areaId]);
            } catch {
                // Unparseable field: skip it rather than throwing. This runs at
                // master BOOT, and a single corrupt/hand-written field must not
                // be able to stop a master from starting.
                continue;
            }
            if (!parsed || typeof parsed !== 'object') continue;
            out.push({
                areaId,
                opts: { drain: parsed.opts?.drain, reason: parsed.opts?.reason },
                generation: Number(parsed.generation),
            });
        }
        return out;
    }

    async clearDestroyIntent(areaId: string): Promise<void> {
        await this.redis.hdel(this.intentsKey(), areaId);
    }
}
